// import type { Enums } from '@/types_db';
//
// type TYPE = Enums<'train_type'>;

export enum TRAIN_TYPE {
  DRY_WET = 'dry-wet',
  SWEEP_V3 = 'sweep-v3',
  CONVERT = 'convert',
}

export enum GEAR {
  AMP = 'amp',
  AMP_CAB = 'amp-cab',
  PEDAL = 'pedal',
  OUTBOARD = 'outboard',
  CAB = 'cab',
  SPACE = 'space',
  EXPERIMENTAL = 'experimental',
}

export enum FORMAT {
  NAM = 'nam',
  AIDAX = 'aida-x',
  AASNAPSHOT = 'aa-snapshot',
  IR = 'ir',
  PROTEUS = 'proteus',
}

/**
 * Per-Gear allowed Formats. Enforced on upload/edit (UI + server validator).
 *
 * Legacy `amp|ir` rows are grandfathered — the matrix disallows them for new
 * uploads, but the edit form skips the check unless the user actually changes
 * Gear or Format.
 */
export const GEAR_FORMAT_COMPATIBILITY: Record<GEAR, readonly FORMAT[]> = {
  [GEAR.AMP_CAB]: [FORMAT.NAM, FORMAT.AIDAX, FORMAT.AASNAPSHOT, FORMAT.PROTEUS],
  [GEAR.AMP]: [FORMAT.NAM, FORMAT.AIDAX, FORMAT.AASNAPSHOT, FORMAT.PROTEUS],
  [GEAR.PEDAL]: [FORMAT.NAM, FORMAT.AIDAX, FORMAT.AASNAPSHOT, FORMAT.PROTEUS, FORMAT.IR],
  [GEAR.OUTBOARD]: [FORMAT.NAM, FORMAT.AIDAX, FORMAT.AASNAPSHOT, FORMAT.PROTEUS, FORMAT.IR],
  [GEAR.CAB]: [FORMAT.NAM, FORMAT.AIDAX, FORMAT.AASNAPSHOT, FORMAT.PROTEUS, FORMAT.IR],
  [GEAR.SPACE]: [FORMAT.IR],
  [GEAR.EXPERIMENTAL]: [
    FORMAT.NAM,
    FORMAT.AIDAX,
    FORMAT.AASNAPSHOT,
    FORMAT.PROTEUS,
    FORMAT.IR,
  ],
} as const;

/**
 * A Gear outside the catalog supports no Format. Reached with a raw value from
 * a URL token or a request body, where the cast at the call site is a claim TS
 * cannot check — an unmapped key must answer the question, not throw.
 */
export function isGearFormatCompatible(gear: GEAR, format: FORMAT): boolean {
  return GEAR_FORMAT_COMPATIBILITY[gear]?.includes(format) ?? false;
}

/**
 * Is this tone a pack of impulse responses?
 *
 * IR-ness is a Format, not a Gear. `gear = 'ir'` is retired, so an IR pack now
 * carries its real gear (cab, space, pedal, …) and says `platform = 'ir'`.
 * Anything asking "is this an IR?" must ask the Format, or it silently answers
 * false for the entire IR catalog.
 *
 * `platform` is nullable — a pack with no models yet is not an IR pack.
 */
export function isIrTone(tone: { platform?: string | null }): boolean {
  return tone.platform === FORMAT.IR;
}

/**
 * The catalog Gears in display order. Lives here, not in a `'use client'`
 * module, so Server Components can import
 * it as a real value (a value re-exported from a client module becomes a
 * client-reference proxy in RSC).
 */
export const GEAR_DISPLAY_ORDER: GEAR[] = [
  GEAR.AMP_CAB,
  GEAR.AMP,
  GEAR.CAB,
  GEAR.PEDAL,
  GEAR.OUTBOARD,
  GEAR.SPACE,
  GEAR.EXPERIMENTAL,
];

/** Gears a tone may be created with or set to on edit. */
export const CREATABLE_GEARS: GEAR[] = GEAR_DISPLAY_ORDER;

export enum MODEL_SIZE {
  STANDARD = 'standard',
  LITE = 'lite',
  FEATHER = 'feather',
  NANO = 'nano',
  CUSTOM = 'custom',
}

/**
 * Architecture version. Source of truth is the DB enum `architecture_version`
 * (see migration `20260505175906_architecture_version_enum.sql`); the type
 * here is anchored to that via the generated `Database` types so the canonical
 * TS type can't drift from the DB.
 *
 * The `satisfies` clause makes the constants object compile-error if any
 * value isn't a valid DB enum label — so renaming/removing a label in the DB
 * forces a corresponding update here.
 */
export type ArchitectureVersion =
  import('@/types_db').Database['public']['Enums']['architecture_version'];

export const ARCHITECTURE_VERSION = {
  A1: '1',
  A2: '2',
  CUSTOM: 'custom',
} as const satisfies Record<string, ArchitectureVersion>;

/** Architecture filter inputs (URL ?architecture= and OAuth flow). */
export type ArchitectureFilter = ArchitectureVersion;

export enum TRAIN_ERROR {
  MISMATCH = 'mismatch',
  INCONSISTENT = 'inconsistent',
  // Legacy "your recording is too short" failure from the v1 trainer.
  // No longer emitted by preprocessing (the v2 frontend enforces a
  // 2-minute minimum upload length in `utils/audio.ts`), but kept so
  // historical `trainings.error = 'short'` rows still render a sensible
  // message. The dry-wet edge-silence check now uses `SILENCE` instead.
  SHORT = 'short',
  // Dry-wet recording has too many literal-zero samples at the leading
  // or trailing edge of either side. Emitted by the preprocess
  // edge-silence check (`trigger/python/train_preprocess.py`).
  SILENCE = 'silence',
  // Input or output audio clips (a sample peaks at/above 0 dBFS).
  // NAM hard-fails on clipped targets, so preprocessing rejects it
  // before any GPU time is burned.
  CLIPPED = 'clipped',
  AUDIO = 'audio',
  LENGTH = 'length',
}

export enum TRAIN_PROVIDER {
  COG = 'cog',
  BEAM = 'beam',
  HF = 'hf',
  RUNPOD = 'runpod',
  GOOGLE = 'google',
  AWS = 'aws',
}

export enum LICENSE {
  T3K = 't3k',
  CC_BY = 'cc-by',
  CC_BY_SA = 'cc-by-sa',
  CC_BY_NC = 'cc-by-nc',
  CC_BY_NC_SA = 'cc-by-nc-sa',
  CC_BY_ND = 'cc-by-nd',
  CC_BY_NC_ND = 'cc-by-nc-nd',
  CCO = 'cco',
}

export const GEAR_TITLE_MAP: Record<GEAR, string> = {
  [GEAR.AMP]: 'Amp Head',
  [GEAR.AMP_CAB]: 'Amp + Cab',
  [GEAR.PEDAL]: 'Pedal',
  [GEAR.OUTBOARD]: 'Outboard',
  [GEAR.CAB]: 'Cabinet',
  [GEAR.SPACE]: 'Spaces',
  [GEAR.EXPERIMENTAL]: 'Experimental',
};

export const GEAR_CAPTURE_MAP: Record<GEAR, string> = {
  [GEAR.AMP]: 'Amp Head Capture',
  [GEAR.AMP_CAB]: 'Amp + Cab Capture',
  [GEAR.PEDAL]: 'Pedal Capture',
  [GEAR.OUTBOARD]: 'Outboard Capture',
  [GEAR.CAB]: 'Cabinet',
  [GEAR.SPACE]: 'Spaces',
  [GEAR.EXPERIMENTAL]: 'Experimental Capture',
};

export const GEAR_CAPTURE_SMALL_MAP: Record<GEAR, string> = {
  [GEAR.AMP]: 'Amp Head',
  [GEAR.AMP_CAB]: 'Amp + Cab',
  [GEAR.PEDAL]: 'Pedal Capture',
  [GEAR.OUTBOARD]: 'Outboard Capture',
  [GEAR.CAB]: 'Cabinet',
  [GEAR.SPACE]: 'Spaces',
  [GEAR.EXPERIMENTAL]: 'Experimental',
};

/**
 * Upload/edit help copy. The format-specific subtext shows under the Gear
 * picker and swaps based on the detected/selected Format. Kept to a short,
 * single-line hint (matching the original gear subtexts). A blocked combo's
 * subtext is rarely seen because the picker only offers compatible Gears.
 */
export const GEAR_HELP_TEXT: Record<
  GEAR,
  { namSubtext: string | null; irSubtext: string | null }
> = {
  [GEAR.AMP_CAB]: {
    namSubtext: "Mic'd combo amp or amp head with cabinet",
    irSubtext: null,
  },
  [GEAR.AMP]: {
    namSubtext: 'Direct amp head output, no cab',
    irSubtext: null,
  },
  [GEAR.PEDAL]: {
    namSubtext: 'Distortion, fuzz, overdrive etc.',
    irSubtext: 'Delay, reverb, EQ pedals etc.',
  },
  [GEAR.OUTBOARD]: {
    namSubtext: 'Compressor, preamp, tape etc.',
    irSubtext: 'Rack EQ, reverb, spring tank etc.',
  },
  [GEAR.CAB]: {
    namSubtext: 'Speaker cabinet + mic',
    irSubtext: 'Speaker cabinet + mic',
  },
  [GEAR.SPACE]: {
    namSubtext: null,
    irSubtext: 'Rooms, halls, cathedrals etc.',
  },
  [GEAR.EXPERIMENTAL]: {
    namSubtext: "Creative captures that don't fit other categories",
    irSubtext: "Creative captures that don't fit other categories",
  },
};

export const LICENSE_COPY_MAP = {
  [LICENSE.T3K]:
    "Users may download and use the data file in software and publish the resulting outputs without royalties or restrictions. However, they may not upload, republish, or distribute the data file without the author's permission.",
  [LICENSE.CC_BY]:
    'This license allows reusers to distribute, remix, adapt, and build upon the material in any medium or format, so long as attribution is given to the creator. The license allows for commercial use.',
  [LICENSE.CC_BY_SA]:
    'This license allows reusers to distribute, remix, adapt, and build upon the material in any medium or format, so long as attribution is given to the creator. The license allows for commercial use. If you remix, adapt, or build upon the material, you must license the modified material under identical terms.',
  [LICENSE.CC_BY_NC]:
    'This license allows reusers to distribute, remix, adapt, and build upon the material in any medium or format for noncommercial purposes only, and only so long as attribution is given to the creator.',
  [LICENSE.CC_BY_NC_SA]:
    'This license allows reusers to distribute, remix, adapt, and build upon the material in any medium or format for noncommercial purposes only, and only so long as attribution is given to the creator. If you remix, adapt, or build upon the material, you must license the modified material under identical terms.',
  [LICENSE.CC_BY_ND]:
    'This license allows reusers to copy and distribute the material in any medium or format in unadapted form only, and only so long as attribution is given to the creator. The license allows for commercial use.',
  [LICENSE.CC_BY_NC_ND]:
    'This license allows reusers to copy and distribute the material in any medium or format in unadapted form only, for noncommercial purposes only, and only so long as attribution is given to the creator.',
  [LICENSE.CCO]:
    'This license is a public dedication tool, which allows creators to give up their copyright and put their works into the worldwide public domain. CC0 allows reusers to distribute, remix, adapt, and build upon the material in any medium or format, with no conditions.',
};

export const TONE_ZONE_ANSI = `████████╗ ██████╗ ███╗   ██╗███████╗
╚══██╔══╝██╔═══██╗████╗  ██║██╔════╝
   ██║   ██║   ██║██╔██╗ ██║█████╗  
   ██║   ██║   ██║██║╚██╗██║██╔══╝  
   ██║   ╚██████╔╝██║ ╚████║███████╗
   ╚═╝    ╚═════╝ ╚═╝  ╚═══╝╚══════╝
██████╗  ██████╗  ██████╗  ██████╗  
╚════██╗██╔═████╗██╔═████╗██╔═████╗ 
 █████╔╝██║██╔██║██║██╔██║██║██╔██║ 
 ╚═══██╗████╔╝██║████╔╝██║████╔╝██║ 
██████╔╝╚██████╔╝╚██████╔╝╚██████╔╝ 
╚═════╝  ╚═════╝  ╚═════╝  ╚═════╝
`;

export const EMAIL = `support@tone3000.com`;

export const MAX_MODELS_PER_TONE = 300;

/** max_epochs bounds for trainings; the capture UI options and the trainings API validation both derive from these. */
export const DEFAULT_MAX_EPOCHS = 200;
export const MAX_MAX_EPOCHS = 400;

/**
 * display_name renders publicly wherever a creator name appears, and the profile
 * heading ellipsizes past roughly 20 characters at desktop width. This isn't
 * sized to prevent that truncation — it's the ceiling that keeps a name a name.
 */
export const DISPLAY_NAME_MAX_LENGTH = 32;

/** Own-profile tabs in display order; the first one is the default with no `tab` param. */
export const PROFILE_TABS = ['downloads', 'favorites', 'created'] as const;
export type ProfileTab = (typeof PROFILE_TABS)[number];
export const DEFAULT_PROFILE_TAB: ProfileTab = PROFILE_TABS[0];

/** Reads the `tab` search param, falling back to the default for missing or unknown values. */
export const toProfileTab = (value: string | null | undefined): ProfileTab =>
  PROFILE_TABS.find((tab) => tab === value) ?? DEFAULT_PROFILE_TAB;

/** The capture guides that replaced the retired /capture/instructions page. */
export const SWEEP_CAPTURE_GUIDE_URL = '/guides/capture-your-gear-with-nam-sweep-method';
export const DRY_WET_CAPTURE_GUIDE_URL = '/guides/capture-your-gear-with-nam-dry-wet';

/** CMS guides linked from marketing copy (FAQ, About). */
export const GET_STARTED_GUIDE_URL = '/guides/get-started-with-tone3000';
export const PLUGIN_GUIDE_URL = '/guides/tone3000-plugin';
export const IR_GUIDE_URL = '/guides/impulse-responses';
export const IR_SPEAKER_CABINET_CAPTURE_GUIDE_URL =
  '/guides/how-to-capture-impulse-response-ir-speaker-cabinet';
export const IR_ACOUSTIC_SPACE_CAPTURE_GUIDE_URL =
  '/guides/how-to-capture-impulse-response-ir-acoustic-space';
export const IR_EFFECTS_UNIT_CAPTURE_GUIDE_URL =
  '/guides/how-to-capture-impulse-response-ir-effects-unit';
export const NAM_GUIDE_URL = '/guides/neural-amp-modeler';
export const A2_GUIDE_URL = '/guides/nam-a2-the-complete-guide';
export const A2_ANNOUNCEMENT_URL = '/blog/introducing-neural-amp-modeler-nam-architecture-2-a2';
/** Section anchors inside the A2 guide; slugs match the CMS heading slugifier. */
export const A2_UNSUPPORTED_HARDWARE_URL = `${A2_GUIDE_URL}#what-if-my-hardware-or-software-doesn-t-support-a2`;
export const A2_LISTENING_TEST_URL = `${A2_GUIDE_URL}#how-do-a2-full-and-a2-lite-sound`;

/** Open-source ratings behind the A2 listening and accuracy tests. */
export const MUSHRA_TEST_DATA_URL = 'https://github.com/tone-3000/a2-mushra-data';
export const GITHUB_ORG_URL = 'https://github.com/tone-3000';
